import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData, DefaultResponse } from "../models/system";

export interface IVatMarginPeriodSummary {
  from: string;
  to: string;
  schemeEnabled: boolean;
  standardRate: number;
  openingPoolBalance: number;
  configuredOpeningBalance: number;
  purchasesEntered: number;
  purchasesRemoved: number;
  eligiblePurchases: number;
  eligibleSales: number;
  grossMargin: number;
  vatDue: number;
  closingPoolBalance: number;
  purchaseEntryCount: number;
  salesLineCount: number;
}

export interface IResolvedPartSalesTax {
  taxRate: number;
  isMarginScheme: boolean;
  treatment: number;
}

/** Effective sales VAT for one part (override → vehicle stamp → vehicle purchase VAT fallback). */
export function usePartVatResolution(partId?: string) {
  const { get } = useApiHelper();

  const query = useQuery({
    queryKey: ["part-vat-resolution", partId],
    queryFn: () =>
      get<DefaultResponseWithData<IResolvedPartSalesTax>>(`/vat/resolve-part/${partId}`),
    enabled: Boolean(partId),
    staleTime: 1000 * 60, // 1 minute
  });

  return {
    resolution: query.data?.data,
    resolutionLoading: query.isLoading,
  };
}

export interface ISetVehiclePurchaseVatRequest {
  vehicleId: string;
  applyVat: boolean;
  /** Margin pool purchase cost when setting no-VAT and no lead price exists (parts-only customers). */
  purchaseCost?: number | null;
}

export default function useVatMarginScheme(from?: string, to?: string) {
  const { get, post, put } = useApiHelper();
  const queryClient = useQueryClient();

  const periodQuery = useQuery({
    queryKey: ["vat-margin-period", from, to],
    queryFn: () =>
      get<DefaultResponseWithData<IVatMarginPeriodSummary>>(
        `/vat/margin-scheme/period?from=${encodeURIComponent(from!)}&to=${encodeURIComponent(to!)}`
      ),
    enabled: Boolean(from && to),
  });

  const standardRateQuery = useQuery({
    queryKey: ["vat-standard-rate"],
    queryFn: () => get<DefaultResponseWithData<number>>("/vat/standard-rate"),
  });

  const ensureSettings = useMutation(
    () => post("/vat/margin-scheme/ensure-settings", null) as Promise<DefaultResponse>,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["settings"]);
        queryClient.invalidateQueries(["vat-standard-rate"]);
      },
    }
  );

  const closeScheme = useMutation(
    (notes?: string) =>
      post("/vat/margin-scheme/close", {
        notes: notes ?? null,
      }) as Promise<DefaultResponse>,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vat-margin-period"]);
        // Close clears Vat:MarginSchemeOpeningBalance to 0.
        queryClient.invalidateQueries(["settings"]);
      },
    }
  );

  const setVehiclePurchaseVat = useMutation(
    (req: ISetVehiclePurchaseVatRequest) =>
      put(`/vat/vehicle/${req.vehicleId}/purchase-vat`, {
        applyVat: req.applyVat,
        purchaseCost: req.purchaseCost ?? null,
      }) as Promise<DefaultResponse>,
    {
      onSuccess: (_data, req) => {
        queryClient.invalidateQueries(["vehicles", req.vehicleId]);
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["vehicle", req.vehicleId, "lead"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["vat-margin-period"]);
        // The vehicle purchase flag drives every one of its parts' resolved treatment.
        queryClient.invalidateQueries(["part-vat-resolution"]);
        queryClient.invalidateQueries(["omniSearchVat"]);
      },
    }
  );

  return {
    periodSummary: periodQuery.data?.data,
    periodLoading: periodQuery.isLoading,
    refetchPeriod: periodQuery.refetch,
    standardRate: standardRateQuery.data?.data ?? 20,
    standardRateLoading: standardRateQuery.isLoading,
    ensureSettings,
    closeScheme,
    setVehiclePurchaseVat,
  };
}
